// Shared API host resolver for CDN mode
// Resolves API base URL by sending visit/track to candidate subdomains (e.g., api.example.com)
// Exposes: ready (Promise), getApiBaseUrl(), buildApiUrl(path), hasApi()

// Subdomain to use for API host (e.g., www.example.com → api.example.com)
const API_SUBDOMAIN = 'api';
// Frontend subdomain prefix to replace with API_SUBDOMAIN (e.g., www → api)
const FRONTEND_PREFIX = 'www';
let apiBaseUrl = null;
let apiResolved = false;

function buildApiHostname(hostname) {
  if (hostname.startsWith(FRONTEND_PREFIX + '.')) {
    return API_SUBDOMAIN + '.' + hostname.slice(FRONTEND_PREFIX.length + 1);
  }
  return `${API_SUBDOMAIN}.${hostname}`;
}

function buildCandidateBases() {
  const protocol = window.location.protocol || 'https:';
  const hostWithPort = window.location.host; // includes port in dev
  const hostname = window.location.hostname; // bare hostname for CDN
  const sameOrigin = `${protocol}//${hostWithPort}`;
  const apiHost = buildApiHostname(hostname);
  const cdnHosts = [`${protocol}//${apiHost}`];
  // In localhost/dev, prefer same-origin so MSW SW can intercept requests.
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return [sameOrigin, ...cdnHosts];
  }
  return cdnHosts;
}

function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost');
}

async function resolveAndTrack() {
  if (apiResolved) return apiBaseUrl;
  const bases = buildCandidateBases();
  const body = JSON.stringify(collectPagePayload());
  for (const base of bases) {
    try {
      const url = `${base}/api/visit/track`;
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller.signal,
      });
      clearTimeout(id);
      if (!res.ok) continue;
      const data = await res.json().catch(() => ({}));
      apiBaseUrl = base;
      apiResolved = true;
      window.__apiBaseUrl = apiBaseUrl;
      if (data && typeof data.visitId !== 'undefined') {
        window.__visitId = data.visitId;
      }
      if (data && typeof data.offerVisitIds !== 'undefined') {
        window.__visitData = Object.assign({}, window.__visitData || {}, { offerVisitIds: data.offerVisitIds });
        applyOfferVisitIds(data.offerVisitIds);
      }
      if (data && typeof data.serverTime === 'number' && data.serverTime > 0) {
        window.__serverTime = data.serverTime;
      }
      if (data && typeof data.bankiRuInRollout !== 'undefined') {
        window.__bankiRuInRollout = Boolean(data.bankiRuInRollout);
      }
      const { name, amount, restamount, mfoscore, fullamount } = data || {};
      const fields = { name: name ?? null, amount: typeof amount === 'number' ? amount : (amount ? Number(amount) : null), restamount: typeof restamount === 'number' ? restamount : (restamount ? Number(restamount) : null), mfoscore: typeof mfoscore === 'number' ? mfoscore : (mfoscore ? Number(mfoscore) : null), fullamount: typeof fullamount === 'number' ? fullamount : (fullamount ? Number(fullamount) : null) };
      window.__visitData = Object.assign({}, window.__visitData || {}, { fields });
      try {
        const evt = new CustomEvent('visit:tracked', { detail: { visitId: window.__visitId, offerVisitIds: window.__visitData.offerVisitIds, fields, serverTime: window.__serverTime ?? null, bankiRuInRollout: window.__bankiRuInRollout ?? null } });
        window.dispatchEvent(evt);
        document.dispatchEvent(evt);
      } catch (_) {}
      return base;
    } catch (_) {
      continue;
    }
  }
  apiResolved = true;
  apiBaseUrl = null;
  window.__apiBaseUrl = null;
  return null;
}

export function getApiBaseUrl() {
  return apiBaseUrl;
}

export function buildApiUrl(path) {
  if (!apiBaseUrl) return null;
  if (!path.startsWith('/')) return `${apiBaseUrl}/${path}`;
  return `${apiBaseUrl}${path}`;
}

export function hasApi() {
  return Boolean(apiBaseUrl);
}

function collectPageOfferIds() {
  const ids = [];
  document.querySelectorAll('[data-offer-id]').forEach((el) => {
    const id = el.dataset.offerId;
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

function applyOfferVisitIds(offerVisitIds) {
  if (!offerVisitIds || typeof offerVisitIds !== 'object') return;
  Object.keys(offerVisitIds).forEach((productId) => {
    const el = document.querySelector(`[data-offer-id="${productId}"]`);
    if (el) el.dataset.offerVisitId = String(offerVisitIds[productId]);
  });
}

function collectPagePayload() {
  // Раньше эти параметры (ph, Name:z5g, utm_*, _websiteId, alg и т.д.) читал
  // сервер напрямую из query-строки при рендере страницы. Теперь страница
  // статическая (CDN), поэтому их собирает и пересылает фронтенд —
  // как есть, без повторного декодирования (бэкенд ожидает то же
  // представление значений, что раньше давал $request->query->all()).
  const pageParams = Object.fromEntries(new URLSearchParams(window.location.search || ''));
  return { ...pageParams, idsProducts: collectPageOfferIds() };
}

export const ready = (async () => {
  try {
    if (typeof window !== 'undefined' && window.__mswReadyPromise && isLocalhost()) {
      await window.__mswReadyPromise.catch(() => {});
    }
  } catch (_) {}
  const base = await resolveAndTrack();
  if (!base) {
    window.__workOffline = true;
    return null;
  }
  return base;
})();
